<template>
    <div class="selector-wrapper">
        <div class="home-route-selector">
            <div class="selector-header">
                <h2 class="selector-title">{{ t('routes_and_directions') }}</h2>
                <DotsLoading v-if="isLoading" class="loader" />
                <RouteSelector
                    v-else
                    route-name="route-single"
                    class="route-selector"
                    :show-services="false"
                    show-address
                    @change-state="changeState"
                ></RouteSelector>
                <template v-if="currentState === 'initial'">
                    <div class="separator">{{ t('or') }}</div>
                    <h3 class="selector-subtitle">{{ t('plan_trip') }}</h3>
                </template>
            </div>
            <TripPlanner v-if="currentState === 'initial'" />
        </div>
        <div class="services-wrapper">
            <h3 class="services-title">Nos services</h3>
            <ul class="no-bullet service-type-list">
                <li v-for="(serviceType, i) in servicesTypes" :key="`service-item-${i}`" class="service-item" :class="serviceType.class">
                    <button
                        type="button"
                        class="service-type-button button"
                        :class="[serviceType.class, `service-type-button-${serviceType.code}`]"
                        :disabled="isLoading"
                        @click="openServiceRoutes(serviceType.code, $event)"
                    >
                        <img :src="serviceType.icon" width="45" height="45" aria-hidden="true" alt="" />
                        <span class="service-name">{{ serviceType.name }}</span>
                    </button>
                    <div
                        v-if="isRouteListOpen && activeService === serviceType.code"
                        ref="floatingList"
                        class="route-list-wrapper"
                        :style="floatingStyles"
                        @focusout="onRouteListFocusOut"
                    >
                        <div class="route-list-header">
                            <img :src="serviceType.icon" width="24" height="24" aria-hidden="true" alt="" />
                            <span class="service-name">{{ serviceType.name }}</span>
                            <button ref="closeButton" class="close-button" @click="closeServiceRoutes">
                                <IconClose width="12" height="12" />
                                <span class="visuallyhidden">{{ $t('a11y.close_window') }}</span>
                            </button>
                        </div>
                        <button v-if="showSchool" type="button" class="go-back-button" @click="closeSchoolRoutes">
                            <IconDropdownArrow class="back-icon" width="8" height="4" />{{ t('return_lebus') }}
                        </button>
                        <button
                            v-if="activeService === 0 && filterRoute(2).length && !showSchool"
                            type="button"
                            class="school-route-button"
                            @click="openSchoolRoutes"
                        >
                            {{ t('school_routes') }}
                            <IconDropdownArrow class="school-button-icon" width="6" height="12" />
                        </button>
                        <AccordionList :key="String(showSchool)" class="route-list" individual>
                            <ServiceTypeItem
                                v-for="route in showSchool ? filterRoute(2) : filterRoute(activeService)"
                                :route="route"
                                :service-type="route.codeTypeService"
                                :service-type-infos="getServiceType(route.codeTypeService)"
                                route-name="route-single"
                            ></ServiceTypeItem>
                        </AccordionList>
                    </div>
                </li>
                <li>
                    <BaseLink
                        :href="locale === 'fr' ? 'https://aveloquebec.ca/' : 'https://aveloquebec.ca/en/'"
                        external
                        :icon="false"
                        class="avelo-link button"
                    >
                        <img src="@@/img/avelo.svg" width="45" height="45" aria-hidden="true" alt="" />
                        <span class="service-name">àVélo</span>
                    </BaseLink>
                </li>
                <li>
                    <a
                        :href="locale === 'fr' ? '/flexibus' : '/en/flexibus'"
                        class="flexibus-link button"
                    >
                        <img src="@@/img/flexibus.svg" width="45" height="45" aria-hidden="true" alt="" />
                        <span class="service-name">Flexibus</span>
                    </a>
                </li>
            </ul>
        </div>
    </div>
</template>

<script setup>
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { ref, computed, onMounted, nextTick } from 'vue';
import { useFloating, flip, offset, shift } from '@floating-ui/vue';
import { onClickOutside } from '@vueuse/core';

import { useSchedulesStore } from '@@/js/stores/schedules';
import AccordionList from '@/shared/AccordionList';
import BaseLink from '@/shared/BaseLink';
import DotsLoading from '@/shared/DotsLoading';
import RouteSelector from '@/shared/RouteSelector';
import ServiceTypeItem from '@/shared/ServiceTypeItem';
import TripPlanner from '@/shared/TripPlanner';

import IconClose from '@@/icons/close.svg';
import IconDropdownArrow from '@@/icons/dropdown-arrow.svg';

const { t, locale } = useI18n({
    messages: {
        fr: {
            routes_and_directions: 'Parcours et itinéraire',
            or: 'ou',
            plan_trip: 'Planifier votre itinéraire',
            nearby: 'À proximité',
            ntr_nearby: 'Temps réel à proximité',
            school_routes: 'Voir les parcours écoles',
            return_lebus: 'Retour aux parcours',
        },
        en: {
            routes_and_directions: 'Routes and directions',
            or: 'or',
            plan_trip: 'Plan your trip',
            nearby: 'Nearby',
            ntr_nearby: 'Real time nearby',
            school_routes: 'See school routes',
            return_lebus: 'Return to routes list',
        },
    },
});

const schedulesStore = useSchedulesStore();
const { fetchSchedulesData, getServiceType } = schedulesStore;
const { routesByServices } = storeToRefs(schedulesStore);

const servicesTypes = computed(() => {
    return routesByServices.value.filter((s) => typeof s.showWith === 'undefined');
});

function filterRoute(serviceTypeCode) {
    const serviceType = routesByServices.value.find((s) => s.code === serviceTypeCode);
    return serviceType?.routes || [];
}

const isRouteListOpen = ref(false);
const activeService = ref(null);
const showSchool = ref(false);

const serviceButton = ref(null);
const floatingList = ref(null);
const closeButton = ref(null);

const floatingListSingle = computed(() => {
    const el = floatingList.value;
    return Array.isArray(el) ? (el[0] ?? null) : el;
});

const { floatingStyles } = useFloating(serviceButton, floatingListSingle, {
    placement: 'bottom-start',
    middleware: [offset(({ rects }) => ({ mainAxis: -rects.reference.height })), flip({ mainAxis: false }), shift({ padding: 16 })],
});

async function openServiceRoutes(serviceTypeCode, event) {
    serviceButton.value = event.currentTarget;
    isRouteListOpen.value = true;
    activeService.value = serviceTypeCode;
    showSchool.value = false;
    await nextTick();
    const btn = Array.isArray(closeButton.value) ? closeButton.value[0] : closeButton.value;
    btn?.focus({ preventScroll: true });
}

function closeServiceRoutes() {
    isRouteListOpen.value = false;
    activeService.value = null;
    showSchool.value = false;
    serviceButton.value?.focus({ preventScroll: true });
}

async function openSchoolRoutes() {
    showSchool.value = true;
    await nextTick();
    const btn = Array.isArray(closeButton.value) ? closeButton.value[0] : closeButton.value;
    btn?.focus({ preventScroll: true });
}

async function closeSchoolRoutes() {
    showSchool.value = false;
    await nextTick();
    const btn = Array.isArray(closeButton.value) ? closeButton.value[0] : closeButton.value;
    btn?.focus({ preventScroll: true });
}

function onRouteListFocusOut() {
    setTimeout(() => {
        if (!floatingListSingle.value?.contains(document.activeElement)) {
            closeServiceRoutes();
        }
    }, 0);
}

onClickOutside(floatingListSingle, closeServiceRoutes, {
    ignore: ['.service-type-button'],
});

const currentState = ref('initial');

function changeState(state) {
    currentState.value = state;
}

const isLoading = ref(true);

onMounted(async () => {
    await fetchSchedulesData();
    isLoading.value = false;
});
</script>

<style lang="scss" scoped>
.selector-wrapper {
    display: flex;
    flex-direction: column;
    gap: 32px 40px;
    @include breakpoint(xlarge) {
        flex-direction: row;
        align-items: flex-end;
        justify-content: space-between;
    }
}
.home-route-selector {
    background-color: $color-white;
    border-radius: 20px;
    margin: -20px -16px 0;
    width: calc(100% + 32px);
    position: relative;
    z-index: 1;
    box-shadow: $floating-shadow;
    @include breakpoint(medium) {
        width: 550px;
        margin: 70px 0 0;
    }
}
.loader {
    height: 80px;
}
.selector-header {
    position: relative;
    z-index: 13;
    padding: 24px 16px 0;
    @include breakpoint(medium) {
        padding: 24px 24px 0;
    }
}
.selector-title {
    @include rem-typo(18px);
    color: $color-blue-60;
    font-weight: 700;
    @include breakpoint(medium) {
        @include rem-typo(24px);
    }
}
.selector-subtitle {
    @include rem-typo(16px);
    font-weight: 800;
    margin: 0;
}
.separator {
    @include rem-typo(14px);
    position: relative;
    text-align: center;
    color: $color-black-50;
    font-weight: 700;
    margin: 24px 0;
    &::before,
    &::after {
        content: '';
        width: calc(50% - 32px);
        height: 1px;
        background-color: $color-black-10;
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
    }
    &::before {
        left: 0;
    }
    &::after {
        right: 0;
    }
}
.services-wrapper {
    flex-grow: 1;
}
.services-title {
    @include rem-typo(16px);
}
.service-type-list {
    display: grid;
    gap: 16px;
    grid-template-columns: repeat(3, 1fr);
    @include breakpoint(large) {
        gap: 20px;
        grid-template-columns: repeat(6, 1fr);
    }
}
.service-item {
    display: flex;
    flex-direction: column;
    justify-content: center;
    @include colorize {
        .service-type-button {
            &:hover,
            &:focus-visible {
                background-color: get-color-variation('lighter');
            }
        }
        .route-list-wrapper {
            background-color: get-color-variation('base');
        }
        .service-name {
            color: get-color-variation('base');
        }
    }
}
.service-type-button,
.avelo-link,
.flexibus-link {
    @include rem-typo(14px);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    width: 100%;
    height: 100%;
    padding: 20px 10px;
    text-align: center;
    border-radius: 16px;
    border: 0;
    background-color: $color-white;
    box-shadow: $floating-shadow;
    @include breakpoint(xlarge) {
        padding: 20px;
    }
}
.avelo-link {
    color: $color-watercourse;
    &:hover,
    &:focus-visible {
        background-color: $color-iceberg;
    }
}
.flexibus-link {
    color: $color-yukon-gold;
    &:hover,
    &:focus-visible {
        background-color: $color-merino;
    }
}
.service-name {
    font-weight: 600;
}
.route-list-wrapper {
    height: 380px;
    width: 360px;
    max-width: calc(100vw - 32px);
    border-radius: 20px;
    box-shadow: $dropdown-shadow;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    z-index: 100;
}
.route-list-header {
    display: flex;
    align-items: center;
    gap: 8px;
    background-color: $color-white;
    padding: 14px 20px;
}
.go-back-button,
.school-route-button {
    @include accessible-focus;
    @include rem-typo(14px);
    display: flex;
    align-items: center;
    width: 100%;
    text-align: left;
    color: $color-blue-60;
    border: 0;
    background-color: $color-black-2;
    flex-shrink: 0;
    padding: 12px 16px;
    gap: 8px;
}
.back-icon {
    transform: rotate(90deg);
}
.school-button-icon {
    transform: rotate(-90deg);
}
.route-list {
    flex-grow: 1;
    overflow-y: auto;
    :deep(.accordion-button) {
        @include accessible-focus;
        border-radius: 0;
    }
}
.close-button {
    margin-left: auto;
    background-color: transparent;
    border: 0;
}
</style>
